import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X, Calendar, Youtube } from 'lucide-react';
import {
  GalleryImage,
  getYouTubeEmbedUrl,
  getYouTubeThumbnail,
  getGalleryImageUrl,
  getGalleryThumbnailUrl,
} from '../../services/galleryService';
import countryColors from '../../config/country_colors.json';

interface VideosSectionProps {
  videos: GalleryImage[];
}

interface CountryGroup {
  country: string;
  description?: string;
  videos: GalleryImage[];
}

interface YearGroup {
  year: string;
  countries: CountryGroup[];
}

interface VideoModalProps {
  video: GalleryImage;
  isOpen: boolean;
  onClose: () => void;
}

const getCountryColors = (country: string): { primary: string; secondary: string | null } => {
  const colors = (countryColors as Record<string, { primary: string; secondary: string | null }>)[country];
  return colors || { primary: '#f59e0b', secondary: null };
};

const getVideoThumbnail = (video: GalleryImage): string => {
  const youtubeThumb = getYouTubeThumbnail(video.youtubeUrl || '', 'hq');
  if (youtubeThumb) return youtubeThumb;
  if (video.filename) return getGalleryThumbnailUrl(video);
  return '';
};

const VideoModal: React.FC<VideoModalProps> = ({ video, isOpen, onClose }) => {
  const embedUrl = getYouTubeEmbedUrl(video.youtubeUrl || '');
  const playerSrc = embedUrl ? `${embedUrl}?autoplay=1&rel=0` : '';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="video-backdrop"
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <div key="video-content" className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              className="relative bg-slate-900 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden pointer-events-auto"
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: 'spring', duration: 0.5 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full transition-all group"
                aria-label="Close modal"
              >
                <X className="w-6 h-6 text-white" />
              </button>

              <div className="relative w-full aspect-video bg-black">
                {playerSrc ? (
                  <iframe
                    src={playerSrc}
                    title={`${video.country} ${video.year} - Travel Video`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    className="w-full h-full"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-slate-300 p-6 text-center">
                    <div>
                      <Youtube className="w-12 h-12 mx-auto mb-3 text-red-500" />
                      <p>Unable to load this YouTube video.</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-6 gradient-slate-dark">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2 font-serif">
                      {video.country}
                    </h3>
                    {video.description && (
                      <p className="text-slate-300 mb-3">{video.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-slate-400">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">{video.year}</span>
                      </div>
                    </div>
                  </div>
                  <Youtube className="w-8 h-8 text-red-500" />
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

const VideosSection: React.FC<VideosSectionProps> = ({ videos }) => {
  const [selectedVideo, setSelectedVideo] = useState<GalleryImage | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const yearGroups = useMemo((): YearGroup[] => {
    const withYoutube = videos.filter((v) => v.youtubeUrl && v.youtubeUrl.trim() !== '');

    const byYear: Record<string, Record<string, GalleryImage[]>> = {};
    withYoutube.forEach((video) => {
      if (!byYear[video.year]) byYear[video.year] = {};
      if (!byYear[video.year][video.country]) byYear[video.year][video.country] = [];
      byYear[video.year][video.country].push(video);
    });

    return Object.entries(byYear)
      .sort(([a], [b]) => Number(b) - Number(a))
      .map(([year, countriesMap]) => ({
        year,
        countries: Object.entries(countriesMap)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([country, countryVideos]) => ({
            country,
            description: countryVideos.find((v) => v.description?.trim())?.description,
            videos: countryVideos,
          })),
      }));
  }, [videos]);

  const handleVideoClick = (video: GalleryImage) => {
    setSelectedVideo(video);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedVideo(null), 300);
  };

  if (yearGroups.length === 0) {
    return (
      <div className="text-center py-16">
        <Youtube className="w-16 h-16 mx-auto mb-4 text-slate-400 dark:text-slate-500" />
        <p className="text-lg text-slate-600 dark:text-slate-400">
          No videos available yet. Check back soon!
        </p>
      </div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-16"
      >
        {yearGroups.map((yearGroup) => (
          <section key={yearGroup.year}>
            <div className="flex items-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white font-serif mr-4">
                {yearGroup.year}
              </h2>
              <div className="flex-1 h-px bg-slate-300 dark:bg-slate-600" />
            </div>

            <div className="space-y-12">
              {yearGroup.countries.map((group) => {
                const colors = getCountryColors(group.country);
                return (
                  <div key={`${yearGroup.year}-${group.country}`}>
                    <h3
                      className="text-xl md:text-2xl font-bold font-serif mb-2"
                      style={{ color: colors.primary }}
                    >
                      {group.country}
                    </h3>
                    {group.description && (
                      <p className="text-slate-600 dark:text-slate-400 mb-5 max-w-3xl">
                        {group.description}
                      </p>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {group.videos.map((video, index) => (
                        <motion.div
                          key={video.id}
                          className="group cursor-pointer transition-all duration-300"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05, duration: 0.5 }}
                          onClick={() => handleVideoClick(video)}
                        >
                          <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                            <div className="relative aspect-video bg-slate-800">
                              <img
                                src={getVideoThumbnail(video)}
                                alt={`${video.country} ${video.year}`}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                loading="lazy"
                                onError={(e) => {
                                  const img = e.currentTarget;
                                  const fallback = getYouTubeThumbnail(video.youtubeUrl || '', 'default');
                                  if (fallback && img.src !== fallback) {
                                    img.src = fallback;
                                    return;
                                  }
                                  if (video.filename) {
                                    const galleryFallback = getGalleryImageUrl(video.filename);
                                    if (galleryFallback && img.src !== galleryFallback) {
                                      img.src = galleryFallback;
                                    }
                                  }
                                }}
                              />
                              <div className="absolute inset-0 gradient-overlay-video opacity-60 group-hover:opacity-80 transition-opacity" />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <motion.div
                                  className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center shadow-2xl"
                                  whileHover={{ scale: 1.1 }}
                                  transition={{ type: 'spring', stiffness: 300 }}
                                >
                                  <Play className="w-10 h-10 text-white fill-white ml-1" />
                                </motion.div>
                              </div>
                            </div>

                            <div className="bg-white dark:bg-slate-800 p-5">
                              <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                                <Calendar className="w-4 h-4" />
                                <span className="text-sm">{video.year}</span>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </motion.div>

      {selectedVideo && (
        <VideoModal
          video={selectedVideo}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};

export default VideosSection;
